import { featureProvider } from "@protontech/autofill/features/v1";
import type { DetectionClass, FeatureComputer, ModelProvider } from "@protontech/autofill/types";
import { detectionClasses } from "@protontech/autofill/types";
import type { Result } from "@protontech/autofill/types/utils";
import type { Features, Inferrer } from "@protontech/ml-inference";

/**
 * Assembles a ModelProvider from a per-class params record and an inferrer factory. Shared by
 * the compiled-in providers (built from statically imported weight JSON) and the runtime
 * constructors (built from a weight set fetched at runtime) so both paths produce identical
 * ModelProvider shapes. Returns `ok: false` if `params` doesn't have exactly the `DetectionClass`
 * keys.
 */
export const buildModelProvider = <Params>(params: Record<DetectionClass, Params>, makeInferrer: (params: Params) => Inferrer<Features, number>): Result<ModelProvider, string> => {
  const found = new Set(Object.keys(params));
  const expected = new Set<string>(detectionClasses);
  const missing = detectionClasses.filter((klass) => !found.has(klass));
  // Sorted so the same underlying problem always renders as the same message text, regardless of
  // key insertion order — telemetry groups on this message verbatim.
  const extra = [...found].filter((klass) => !expected.has(klass)).sort();
  if (missing.length > 0 || extra.length > 0) {
    return { ok: false, error: `invalid weight set: missing=[${missing.join(", ")}] extra=[${extra.join(", ")}]` };
  }

  const models = Object.fromEntries(detectionClasses.map((klass) => [klass, { model: makeInferrer(params[klass]), featureComputer: featureProvider[klass] }])) as unknown as Record<
    DetectionClass,
    { model: Inferrer<Features, number>; featureComputer: FeatureComputer }
  >;

  return {
    ok: true,
    value: {
      field: { featureComputer: featureProvider.field },
      form: { featureComputer: featureProvider.form },
      ...models,
    },
  };
};
